const React = require("react");
// react-bootstrap ships an ESM build ("module" field) that webpack picks over
// the CJS one -- plain require() then returns the raw {default,__esModule}
// namespace instead of the component itself. Interop explicitly, same
// pattern already used by CJDropshippingViewModal.js/CJSyncActionModal.js.
const interopDefault = (mod) => (mod && mod.default) ? mod.default : mod;
const Modal = interopDefault(require("react-bootstrap/Modal"));
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { Button } = require("./ui");
const { SHIP_FROM_COUNTRIES, SHIP_FROM_QUICK_COUNTRIES, filterShipFromCountries } = require("../shipFromCountries");
const h = React.createElement;

// "Ship From" filter bar + modal, isolated and reusable (used by
// ProductCollectionPage.js above the product grid). Fully controlled --
// `value`/`onChange` mirror every other FilterBar field (see
// ProductCollectionPage.js's `filters.fromCountryCode` /
// `handleFilterChange`): this component owns none of the applied filter
// state itself, only its own transient UI state below (modal open/closed,
// the pending selection before Confirm, the search text), so there is no
// risk of a second/competing source of truth for the filter.
const ShipFromFilter = ({ value, onChange }) => {
  const { t } = useCJDropshipping();
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [pendingShipFrom, setPendingShipFrom] = React.useState(value);
  const [shipFromSearch, setShipFromSearch] = React.useState("");

  const openModal = () => {
    // Seed the temporary selection from the currently applied filter --
    // Confirm without touching anything re-applies the same value (no-op),
    // never accidentally clears it (see "Si aucun pays n'est sélectionné,
    // conserver le filtre courant" in the spec).
    setPendingShipFrom(value);
    setShipFromSearch("");
    setModalOpen(true);
  };
  // Close (X, backdrop click or Escape -- react-bootstrap routes all three
  // through onHide) discards the pending selection without applying it.
  const closeModal = () => setModalOpen(false);
  const confirmModal = () => {
    onChange(pendingShipFrom);
    setModalOpen(false);
  };

  const selectedCountry = SHIP_FROM_COUNTRIES.find((country) => country.code === value);
  const visibleCountries = filterShipFromCountries(SHIP_FROM_COUNTRIES, shipFromSearch);

  return h(React.Fragment, null,
    h("div", { className: "cjds-shipfrom-bar", role: "tablist", "aria-label": t("h_cj_shipfrom_label", "Ship From") },
      h("button", {
        type: "button",
        role: "tab",
        "aria-selected": value === "",
        className: `cjds-shipfrom-chip${value === "" ? " cjds-shipfrom-chip--active" : ""}`,
        onClick: () => onChange(""),
      }, t("h_cj_shipfrom_all", "All")),
      ...SHIP_FROM_QUICK_COUNTRIES.map((country) => h("button", {
        key: country.code,
        type: "button",
        role: "tab",
        "aria-selected": value === country.code,
        className: `cjds-shipfrom-chip${value === country.code ? " cjds-shipfrom-chip--active" : ""}`,
        onClick: () => onChange(country.code),
      },
        h("span", { className: "cjds-shipfrom-flag", "aria-hidden": "true" }, country.flag),
        country.name
      )),
      h("button", {
        type: "button",
        className: "cjds-shipfrom-more",
        onClick: openModal,
        "aria-haspopup": "dialog",
        "aria-label": t("h_cj_shipfrom_more", "More ship-from countries"),
      },
        selectedCountry
          ? h(React.Fragment, null,
              h("span", { className: "cjds-shipfrom-flag", "aria-hidden": "true" }, selectedCountry.flag),
              selectedCountry.name
            )
          : t("h_cj_shipfrom_more", "More"),
        h("span", { className: "cjds-shipfrom-more-caret", "aria-hidden": "true" }, "▾")
      )
    ),
    h(Modal, { show: isModalOpen, onHide: closeModal, size: "lg", centered: true, dialogClassName: "cjds-shipfrom-modal" },
      h(Modal.Header, { closeButton: true },
        h(Modal.Title, { className: "cjds-shipfrom-modal-title" }, t("h_cj_shipfrom_modal_title", "Ship From"))
      ),
      h(Modal.Body, null,
        h("form", {
          className: "cjds-shipfrom-search-row",
          // Filtering already happens live as `shipFromSearch` changes below --
          // Submit just prevents a real page navigation on Enter, it isn't
          // what gates the filtering itself.
          onSubmit: (event) => event.preventDefault(),
        },
          h("input", {
            type: "text",
            className: "cjds-input",
            value: shipFromSearch,
            onChange: (event) => setShipFromSearch(event.target.value),
            placeholder: t("h_cj_shipfrom_search_placeholder", "Search by country name or code"),
            "aria-label": t("h_cj_shipfrom_search", "Search country"),
          }),
          h(Button, { variant: "secondary", type: "submit" }, t("h_cj_shipfrom_search_btn", "Search"))
        ),
        h("div", { className: "cjds-shipfrom-grid" },
          h("button", {
            type: "button",
            className: `cjds-shipfrom-option${pendingShipFrom === "" ? " cjds-shipfrom-option--selected" : ""}`,
            onClick: () => setPendingShipFrom(""),
          }, t("h_cj_shipfrom_all", "All")),
          ...visibleCountries.map((country) => h("button", {
            key: country.code,
            type: "button",
            className: `cjds-shipfrom-option${pendingShipFrom === country.code ? " cjds-shipfrom-option--selected" : ""}`,
            onClick: () => setPendingShipFrom(country.code),
          },
            h("span", { className: "cjds-shipfrom-flag", "aria-hidden": "true" }, country.flag),
            h("span", { className: "cjds-shipfrom-option-name" }, country.name),
            h("span", { className: "cjds-shipfrom-option-code" }, `(${country.code})`)
          ))
        ),
        !visibleCountries.length
          ? h("p", { className: "cjds-muted" }, t("h_cj_shipfrom_no_match", "No country matches your search."))
          : null
      ),
      h(Modal.Footer, null,
        h(Button, { variant: "secondary", onClick: closeModal }, t("h_cj_shipfrom_close", "Close")),
        h(Button, { onClick: confirmModal }, t("h_cj_shipfrom_confirm", "Confirm"))
      )
    )
  );
};

module.exports = { ShipFromFilter };
